/**
 * ConduitSkillEffect — Fire tree evolution: replaces Lighter with a directed
 * cone flame stream.
 *
 * Upgrade from Lighter: fires a directed stream in a narrow 20° cone (2.5 tiles
 * range) instead of requiring contact. Deals 40% more base damage than Lighter.
 * Applies 2 stacks of `burning` per enemy hit (up from 1).
 *
 * Status effect application is declarative via skill-effect-declarations.json.
 * Inherits Lighter's trunk upgrades — Catchwork's `dotDurationMs` and
 * Tinder's `dotDamagePerSecond` are resolved by the engine under the
 * 'lighter' scope, matching the `replacesId: "lighter"` evolution chain.
 *
 * Cone parameters (confirmed by design-decisions.md):
 * - Length: 2.5 tiles
 * - Angle: 20° (narrow directed stream; broader than Vas Flam at 45°)
 *
 * Fizzle consumes cooldown (the incantation words were spoken). Out-of-range
 * (no targets in cone) does not consume cooldown.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see fire-spec.md § Conduit
 * @see design-decisions.md § Conduit Cone Angle: 20°
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ConduitDeps {
  /**
   * Dispatch declarative status effects via SkillEffectDeclarationEngine.
   * The engine reads skill-effect-declarations.json and resolves
   * params/conditions — the skill no longer manages effect application.
   */
  readonly dispatchEffects: (trigger: string, targetId?: string) => void;
  /**
   * Returns entity IDs of enemies within the cone. Called on activation.
   * The cone is centered on the player's position, extending in the facing
   * direction by `lengthTiles`, with total arc `angleRangeDeg`.
   */
  readonly getConeTargets: (lengthTiles: number, angleRangeDeg: number, facingAngle: number) => string[];
  /** Player's current facing angle in radians. */
  readonly getPlayerFacing: () => number;
  /** Returns true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ConduitConfig {
  /** Cooldown in ms after activation or fizzle. Default: 400 (fast tier). */
  readonly cooldownMs: number;
  /** Cone length in tiles. */
  readonly coneLength: number;
  /** Cone full arc angle in degrees. */
  readonly coneAngle: number;
}

const DEFAULT_CONFIG: ConduitConfig = {
  cooldownMs: 400,               // fast cooldown tier
  coneLength: 2.5,               // 2.5-tile range
  coneAngle: 20,                 // 20° directed stream (design-decisions.md)
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ConduitSkillEffect implements SkillEffect {
  readonly skillId = 'conduit';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: ConduitDeps;
  private readonly config: ConduitConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _lastHitCount = 0;

  constructor(deps: ConduitDeps, config: Partial<ConduitConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get lastHitCount(): number { return this._lastHitCount; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — cooldown consumed even on fizzle
    if (!this.deps.rollSkillSuccess('conduit')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Break hide before firing
    this.deps.breakHide();

    // Query targets in cone
    const facingAngle = this.deps.getPlayerFacing();
    const targets = this.deps.getConeTargets(
      this.config.coneLength,
      this.config.coneAngle,
      facingAngle,
    );
    this._lastHitCount = targets.length;

    // Out-of-range (no targets in cone) — fizzle; no cooldown, no stamina
    if (targets.length === 0) {
      return false;
    }

    // Dispatch burning DoT per target via declaration engine
    for (const targetId of targets) {
      this.deps.dispatchEffects('hit', targetId);
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
